import {
  BookOpen,
  Globe,
  Search,
  MessageSquare,
  Zap,
  Check,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "../../../i18n";

export default function DocsSection() {
  const { t } = useTranslation();
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-6"></div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {t("routes.docs.components.section1.heading")}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t("routes.docs.components.section1.description")}
          </p>
        </div>

        {/* Key Capabilities - Hero Grid */}
        <div className="mb-16">
          {/* Top Two Main Features */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Article Management - Large Feature */}
            <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-3xl p-12 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-12 -translate-x-12"></div>

              <div className="relative z-10">
                <div className="flex items-center mb-6">
                  <div className="h-16 w-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mr-4">
                    <svg
                      className="h-8 w-8 text-white"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <polyline
                        points="14,2 14,8 20,8"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <line
                        x1="16"
                        y1="13"
                        x2="8"
                        y2="13"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <line
                        x1="16"
                        y1="17"
                        x2="8"
                        y2="17"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <polyline
                        points="10,9 9,9 8,9"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold mb-2">
                      {t(
                        "routes.docs.components.section1.articleManagementTitle"
                      )}
                    </h3>
                    <p className="text-blue-100 text-lg">
                      {t(
                        "routes.docs.components.section1.articleManagementSubtitle"
                      )}
                    </p>
                  </div>
                </div>

                <p className="text-xl text-blue-50 mb-8 leading-relaxed">
                  {t(
                    "routes.docs.components.section1.articleManagementDescription"
                  )}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold mb-1">
                      <BookOpen className="h-6 w-6 mx-auto" />
                    </div>
                    <div className="text-sm text-blue-100">
                      {t(
                        "routes.docs.components.section1.articleManagementItems.0"
                      )}
                    </div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold mb-1">
                      <Zap className="h-6 w-6 mx-auto" />
                    </div>
                    <div className="text-sm text-blue-100">
                      {t(
                        "routes.docs.components.section1.articleManagementItems.1"
                      )}
                    </div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold mb-1">
                      <svg
                        className="h-6 w-6 mx-auto"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className="text-sm text-blue-100">
                      {t(
                        "routes.docs.components.section1.articleManagementItems.2"
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Multilingual Support - Large Feature */}
            <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-3xl p-12 text-white relative overflow-hidden">
              <div className="absolute top-0 left-0 w-40 h-40 bg-white/10 rounded-full -translate-y-20 -translate-x-20"></div>
              <div className="absolute bottom-0 right-0 w-28 h-28 bg-white/10 rounded-full translate-y-14 translate-x-14"></div>

              <div className="relative z-10">
                <div className="flex items-center mb-6">
                  <div className="h-16 w-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mr-4">
                    <Globe className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold mb-2">
                      {t("routes.docs.components.section1.multilingualTitle")}
                    </h3>
                    <p className="text-purple-100 text-lg">
                      {t(
                        "routes.docs.components.section1.multilingualSubtitle"
                      )}
                    </p>
                  </div>
                </div>

                <p className="text-xl text-purple-50 mb-8 leading-relaxed">
                  {t("routes.docs.components.section1.multilingualDescription")}
                </p>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                    <div className="text-sm text-purple-100 mb-2">
                      {t(
                        "routes.docs.components.section1.multilingualLabels.languages"
                      )}
                    </div>
                    <div className="text-lg font-semibold">
                      {t(
                        "routes.docs.components.section1.multilingualLabels.languagesDesc"
                      )}
                    </div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                    <div className="text-sm text-purple-100 mb-2">
                      {t(
                        "routes.docs.components.section1.multilingualLabels.aiTranslation"
                      )}
                    </div>
                    <div className="text-lg font-semibold">
                      {t(
                        "routes.docs.components.section1.multilingualLabels.aiTranslationDesc"
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Row - Key Capabilities with original design */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="h-12 w-12 rounded-xl bg-indigo-500 flex items-center justify-center mb-6">
                <Zap className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {t("routes.docs.components.section1.aiSummariesTitle")}
              </h3>
              <p className="text-gray-600 mb-4">
                {t("routes.docs.components.section1.aiSummariesDescription")}
              </p>
              <div className="flex flex-wrap gap-x-3 text-sm text-indigo-600 font-medium">
                <span className="flex items-center">
                  <Check className="w-4 h-4 mr-1" />
                  {t("routes.docs.components.section1.aiSummariesItems.0")}
                </span>
                <span className="flex items-center">
                  <Check className="w-4 h-4 mr-1" />
                  {t("routes.docs.components.section1.aiSummariesItems.1")}
                </span>
                <span className="flex items-center">
                  <Check className="w-4 h-4 mr-1" />
                  {t("routes.docs.components.section1.aiSummariesItems.2")}
                </span>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="h-12 w-12 rounded-xl bg-green-500 flex items-center justify-center mb-6">
                <Search className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {t("routes.docs.components.section1.integratedSearchTitle")}
              </h3>
              <p className="text-gray-600 mb-4">
                {t(
                  "routes.docs.components.section1.integratedSearchDescription"
                )}
              </p>
              <div className="flex flex-wrap gap-x-3 text-sm text-green-600 font-medium">
                <span className="flex items-center">
                  <Check className="w-4 h-4 mr-1" />
                  {t("routes.docs.components.section1.integratedSearchItems.0")}
                </span>
                <span className="flex items-center">
                  <Check className="w-4 h-4 mr-1" />
                  {t("routes.docs.components.section1.integratedSearchItems.1")}
                </span>
                <span className="flex items-center">
                  <Check className="w-4 h-4 mr-1" />
                  {t("routes.docs.components.section1.integratedSearchItems.2")}
                </span>
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="h-12 w-12 rounded-xl bg-orange-500 flex items-center justify-center mb-6">
                <MessageSquare className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {t("routes.docs.components.section1.supportBridgeTitle")}
              </h3>
              <p className="text-gray-600 mb-4">
                {t("routes.docs.components.section1.supportBridgeDescription")}
              </p>
              <div className="flex flex-wrap gap-x-3 text-sm text-orange-600 font-medium">
                <span className="flex items-center">
                  <Check className="w-4 h-4 mr-1" />
                  {t("routes.docs.components.section1.supportBridgeItems.0")}
                </span>
                <span className="flex items-center">
                  <Check className="w-4 h-4 mr-1" />
                  {t("routes.docs.components.section1.supportBridgeItems.1")}
                </span>
                <span className="flex items-center">
                  <Check className="w-4 h-4 mr-1" />
                  {t("routes.docs.components.section1.supportBridgeItems.2")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link
            to="https://dashboard.juhuu.app/"
            className="inline-flex items-center justify-center px-8 py-4 border border-transparent text-lg font-medium rounded-md text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-colors shadow-lg"
          >
            {t("routes.docs.components.section1.cta")}
          </Link>
        </div>
      </div>
    </section>
  );
}
